import { mainApi } from "../mainApi";

const path = "v2/live-dashboard";
const tagName = "LiveDashboard";

export const liveDashboardApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Dashboard CRUD ─────────────────────────────────────────
    getLiveDashboards: builder.query({
      query: (params) => ({
        url: `/${path}`,
        method: "GET",
        params,
      }),
      providesTags: [tagName],
    }),

    getLiveDashboardById: builder.query({
      query: (id) => ({
        url: `/${path}/${id}`,
        method: "GET",
      }),
      providesTags: [tagName],
    }),

    /** Returns React Flow-ready { nodes, edges, viewport, background } */
    getLiveDashboardCanvas: builder.query({
      query: (arg) => {
        const id = typeof arg === "object" && arg !== null ? arg.id : arg;
        const tabId = typeof arg === "object" && arg !== null ? arg.tabId : undefined;
        return {
          url: `/${path}/${id}/canvas`,
          method: "GET",
          params: tabId ? { tabId } : undefined,
        };
      },
      providesTags: [tagName],
    }),

    createLiveDashboard: builder.mutation({
      query: (body) => ({
        url: `/${path}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [tagName],
    }),

    deleteLiveDashboard: builder.mutation({
      query: (id) => ({
        url: `/${path}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [tagName],
    }),

    // ── Layout & Viewport ──────────────────────────────────────
    updateLiveDashboardLayout: builder.mutation({
      query: ({ id, tabId, body }) => ({
        url: `/${path}/${id}/layout`,
        method: "PATCH",
        params: tabId ? { tabId } : undefined,
        body,
      }),
      invalidatesTags: [tagName],
    }),

    updateLiveDashboardViewport: builder.mutation({
      query: ({ id, tabId, body }) => ({
        url: `/${path}/${id}/viewport`,
        method: "PATCH",
        params: tabId ? { tabId } : undefined,
        body,
      }),
      // Viewport-only saves are lightweight — no need to refetch canvas
    }),

    // ── Component (Node) CRUD ──────────────────────────────────
    addLiveDashboardComponent: builder.mutation({
      query: ({ dashboardId, tabId, body }) => ({
        url: `/${path}/${dashboardId}/components`,
        method: "POST",
        params: tabId ? { tabId } : undefined,
        body,
      }),
      invalidatesTags: [tagName],
    }),

    updateLiveDashboardComponent: builder.mutation({
      query: ({ componentId, body }) => ({
        url: `/${path}/components/${componentId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [tagName],
    }),

    deleteLiveDashboardComponent: builder.mutation({
      query: (componentId) => ({
        url: `/${path}/components/${componentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [tagName],
    }),

    // ── Edge (Connection) CRUD ─────────────────────────────────
    addLiveDashboardEdge: builder.mutation({
      query: ({ dashboardId, tabId, body }) => ({
        url: `/${path}/${dashboardId}/edges`,
        method: "POST",
        params: tabId ? { tabId } : undefined,
        body,
      }),
      invalidatesTags: [tagName],
    }),

    updateLiveDashboardEdge: builder.mutation({
      query: ({ edgeId, body }) => ({
        url: `/${path}/edges/${edgeId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [tagName],
    }),

    deleteLiveDashboardEdge: builder.mutation({
      query: (edgeId) => ({
        url: `/${path}/edges/${edgeId}`,
        method: "DELETE",
      }),
      invalidatesTags: [tagName],
    }),
  }),
});

export const {
  // Dashboard
  useGetLiveDashboardsQuery,
  useGetLiveDashboardByIdQuery,
  useGetLiveDashboardCanvasQuery,
  useLazyGetLiveDashboardCanvasQuery,
  useCreateLiveDashboardMutation,
  useDeleteLiveDashboardMutation,

  // Layout & Viewport
  useUpdateLiveDashboardLayoutMutation,
  useUpdateLiveDashboardViewportMutation,

  // Components
  useAddLiveDashboardComponentMutation,
  useUpdateLiveDashboardComponentMutation,
  useDeleteLiveDashboardComponentMutation,

  // Edges
  useAddLiveDashboardEdgeMutation,
  useUpdateLiveDashboardEdgeMutation,
  useDeleteLiveDashboardEdgeMutation,
} = liveDashboardApi;
